/**
 * Focus capacity, the five-rung tree ladder, and progressive overload
 * (PRD `docs/records/prd-platform-evolution.md` §6.7).
 *
 * Source of truth for names, thresholds and blurbs is
 * `ADDENDUM-focus-stages.md` in the design handoff. It takes precedence over
 * both the handoff README §3 and `Today.dc.html`, which draws a leaf-counting
 * sprout.
 *
 * Pure functions only — no Vue, no I/O, injectable inputs. The trees are
 * artwork (`components/focus-stages/`); this module owns the arithmetic and
 * the copy.
 */

export interface FocusStage {
  /** 0-based rung index. */
  index: number;
  /** Tree species. This *is* the rank name — see {@link stageRankCopy}. */
  name: string;
  /** Inclusive lower bound in minutes. */
  minMinutes: number;
  /** Human-readable band, for the ladder rows. */
  range: string;
  /** One line shown against the current rung in the focus panel. */
  blurb: string;
}

export const FOCUS_STAGES: readonly FocusStage[] = [
  {
    index: 0,
    name: "Hazel",
    minMinutes: 0,
    range: "under 10 min",
    blurb: "You're just starting to build the habit. Short blocks count.",
  },
  {
    index: 1,
    name: "Rowan",
    minMinutes: 10,
    range: "10–19 min",
    blurb: "Focus is taking hold. You can hold attention for a useful stretch.",
  },
  {
    index: 2,
    name: "Birch",
    minMinutes: 20,
    range: "20–29 min",
    blurb: "Solid, repeatable blocks. This is where real revision happens.",
  },
  {
    index: 3,
    name: "Oak",
    minMinutes: 30,
    range: "30–44 min",
    blurb: "You can sustain deep concentration — most students never get here.",
  },
  {
    index: 4,
    name: "Sequoia",
    minMinutes: 45,
    range: "45 min+",
    blurb: "Exceptional stamina. Keep it topped up with regular sessions.",
  },
];

/**
 * Completed blocks required before a median means anything. Below this,
 * `capacity_minutes` is null from the API and every surface must render the
 * cold-start state rather than a zero (PRD PE-7.2).
 */
export const MIN_BLOCKS_FOR_CAPACITY = 3;

/** Fixed ladder offered before we have a capacity to derive from. */
export const COLD_START_MINUTES: readonly number[] = [10, 15, 20];

/** Shortest block we will ever offer, so the low end stays plausible. */
const MIN_OPTION_MINUTES = 3;

export function stageIndexFor(capacityMinutes: number): number {
  let index = 0;
  for (const stage of FOCUS_STAGES) {
    if (capacityMinutes >= stage.minMinutes) index = stage.index;
  }
  return index;
}

/** Current rung. A null capacity (cold start) sits at Hazel. */
export function stageFor(capacityMinutes: number | null): FocusStage {
  if (capacityMinutes === null) return FOCUS_STAGES[0];
  return FOCUS_STAGES[stageIndexFor(capacityMinutes)];
}

export function nextStage(capacityMinutes: number | null): FocusStage | null {
  const current = stageFor(capacityMinutes);
  return FOCUS_STAGES[current.index + 1] ?? null;
}

/**
 * Progress through the current rung, 0–1. Saturates at 1 on the top rung so
 * the bar reads "arrived" rather than empty.
 */
export function stageProgress(capacityMinutes: number | null): number {
  if (capacityMinutes === null) return 0;
  const current = stageFor(capacityMinutes);
  const next = nextStage(capacityMinutes);
  if (!next) return 1;
  const span = next.minMinutes - current.minMinutes;
  if (span <= 0) return 1;
  const into = capacityMinutes - current.minMinutes;
  return Math.min(1, Math.max(0, into / span));
}

export interface TimeOption {
  minutes: number;
  /** The deliberate reach past current capacity. */
  stretch: boolean;
}

/**
 * Session-launcher time options, derived from capacity rather than fixed
 * (PRD PE-7.5): `[capacity × 0.7, capacity, capacity + 3 stretch]`.
 *
 * Options are forced strictly ascending. Naively at capacity 3 the formula
 * yields `[3, 3, 6]`, and two identical buttons is a bug the student can see.
 *
 * Callers MUST track the selection by **index**, never by minutes — the
 * absolute values move the moment capacity changes, and storing minutes
 * silently desyncs the selection.
 *
 * Cold start (`null`) returns {@link COLD_START_MINUTES} with no stretch flag:
 * with no capacity, calling 20 minutes a "stretch" would be a guess dressed as
 * a measurement.
 */
export function timeOptions(capacityMinutes: number | null): TimeOption[] {
  if (capacityMinutes === null) {
    return COLD_START_MINUTES.map((minutes) => ({ minutes, stretch: false }));
  }
  const capacity = Math.max(MIN_OPTION_MINUTES, Math.round(capacityMinutes));
  const easier = Math.max(MIN_OPTION_MINUTES, Math.round(capacity * 0.7));
  const atCapacity = Math.max(easier + 1, capacity);
  const stretch = Math.max(atCapacity + 1, capacity + 3);
  return [
    { minutes: easier, stretch: false },
    { minutes: atCapacity, stretch: false },
    { minutes: stretch, stretch: true },
  ];
}

/** The overload rationale shown under the options. Null at cold start. */
export function overloadCopy(capacityMinutes: number | null): string | null {
  if (capacityMinutes === null) return null;
  return `You can currently hold about ${capacityMinutes} minutes. Going slightly past that is what grows your focus.`;
}

export type FocusGoalKind = "capacity" | "consistency";

export interface GoalInput {
  kind: FocusGoalKind;
  capacityMinutes: number | null;
  daysThisWeek: number;
  targetDays: number;
}

export interface GoalCopy {
  title: string;
  blurb: string;
  /** e.g. "22 / 30 min" or "4 / 5 days". */
  progress: string;
  /** 0–1, for the bar and the pips. */
  fraction: number;
}

export function goalCopy(input: GoalInput): GoalCopy {
  const { kind, capacityMinutes, daysThisWeek, targetDays } = input;

  if (kind === "consistency") {
    const fraction =
      targetDays > 0 ? Math.min(1, daysThisWeek / targetDays) : 0;
    return {
      title: `Focus ${targetDays} days a week`,
      blurb:
        "Regular short sessions build the habit faster than occasional long ones.",
      progress: `${daysThisWeek} / ${targetDays} days`,
      fraction,
    };
  }

  const stage = stageFor(capacityMinutes);

  if (capacityMinutes === null) {
    return {
      title: "Find your starting point",
      blurb: `Finish ${MIN_BLOCKS_FOR_CAPACITY} focus blocks and we'll show what you can hold.`,
      progress: "Not measured yet",
      fraction: 0,
    };
  }

  const next = nextStage(capacityMinutes);
  if (!next) {
    // Top rung: a target the student already passed reads as broken, so the
    // goal switches to maintenance framing.
    return {
      title: `Keep blocks above ${stage.minMinutes} min`,
      blurb:
        "You're at the top rung. Regular sessions are what stop this stamina fading.",
      progress: `${capacityMinutes} min held`,
      fraction: 1,
    };
  }

  return {
    title: `Reach ${next.minMinutes}-minute blocks`,
    blurb:
      "Stretch each block a little past your capacity. That's what moves the number.",
    progress: `${capacityMinutes} / ${next.minMinutes} min`,
    fraction: Math.min(1, capacityMinutes / next.minMinutes),
  };
}

export interface OutcomeInput {
  /** Length of the block that just closed, in whole minutes. */
  blockMinutes: number;
  /** Capacity *before* this block, or null at cold start. */
  capacityMinutes: number | null;
  /** Longest completed block before this one. */
  longestMinutes: number;
}

/**
 * The focus line on the session-complete screen (PRD PE-7.6). Three branches,
 * checked longest-first so a personal best is always reported as such.
 */
export function completionOutcome(input: OutcomeInput): string {
  const { blockMinutes, capacityMinutes, longestMinutes } = input;

  if (blockMinutes > longestMinutes) {
    return `New longest block — ${blockMinutes} minutes. Your capacity is growing.`;
  }
  if (capacityMinutes === null) {
    return `${blockMinutes} minutes of focus banked. A few more blocks and we'll know what you can hold.`;
  }
  if (blockMinutes >= capacityMinutes) {
    return `You held ${blockMinutes} minutes, at the top of your current capacity (${capacityMinutes} min).`;
  }
  return `${blockMinutes} minutes of focus banked. Capacity stays at ${capacityMinutes} min — stretch a little further next time.`;
}

/**
 * The rank, as a bare tree name (addendum §2 copy rules): "You're at Oak".
 * Never "Oak stage", never "Level 4", and never a comparison to other
 * students.
 */
export function stageRankCopy(stage: FocusStage): string {
  return `You're at ${stage.name}`;
}

/**
 * Copy for a rung change. Downward movement is phrased gently — a student who
 * drops below a threshold has "eased back to Birch", never "lost Oak".
 * Returns null when the rung did not move, so callers render nothing rather
 * than a no-op celebration.
 */
export function stageChangeCopy(
  fromCapacityMinutes: number | null,
  toCapacityMinutes: number | null,
): string | null {
  const from = stageFor(fromCapacityMinutes);
  const to = stageFor(toCapacityMinutes);
  if (from.index === to.index) return null;
  if (to.index > from.index) return `You've reached ${to.name}`;
  return `Eased back to ${to.name}`;
}

/** True when a block pushed capacity over a rung threshold — drives the stage-up celebration. */
export function isStageUp(
  fromCapacityMinutes: number | null,
  toCapacityMinutes: number | null,
): boolean {
  return (
    stageFor(toCapacityMinutes).index > stageFor(fromCapacityMinutes).index
  );
}
